/**
 * The masthead on the sign-in and sign-up cards.
 *
 * *** WHOSE PAGE IS THIS ***
 * At `/login/<slug>` and `/signup/<slug>` the answer is the INSTITUTE's, so
 * their name and logo go here. The bare `/login` is the platform's own
 * administrator entrance and keeps ours.
 *
 * One component for both pages: they had the same inline SVG copied twice,
 * and a brand that differs between the page you sign up on and the page you
 * sign in on reads as two different products.
 */

import type { InstituteBrand } from './useInstituteBrand'
import styles from './LoginTabs.module.css'

/** Our own mark, for the platform entrance. */
function PlatformMark() {
  return (
    <span className={styles.brandMark} aria-hidden="true">
      <svg viewBox="0 0 100 100" fill="none">
        <path
          d="M50 27.48 A26 26 0 0 1 50 72.52 A26 26 0 0 1 50 27.48 Z"
          fill="#1E3A52"
        />
        <circle cx="37" cy="50" r="26" stroke="#36699C" strokeWidth="7" />
        <circle cx="63" cy="50" r="26" stroke="#1B4D3E" strokeWidth="7" />
      </svg>
    </span>
  )
}

interface Props {
  /** Null on the platform entrance, or while the lookup is in flight. */
  brand: InstituteBrand | null
  /** The line under the title: "Create your student account" and so on. */
  subtitle: string
  /**
   * True while an institute lookup is in flight.
   *
   * *** THIS IS WHY THE TITLE CAN BE BLANK ***
   * Without it the card paints "MentorsConnect", then replaces it with the
   * institute's name a moment later. Showing our brand on their page even
   * for one frame is the thing this whole change removes, so the slot is
   * held empty instead.
   */
  loading?: boolean
}

export function AuthBrand({ brand, subtitle, loading = false }: Props) {
  const title = brand?.name ?? (loading ? ' ' : 'MentorsConnect')

  return (
    <div className={styles.brand}>
      {loading && !brand ? null : brand?.logo_url ? (
        /*
          The institute's own logo. `alt` is empty and the name is beside it
          as text, so a screen reader hears the name once rather than twice.
        */
        <span className={styles.brandMark}>
          <img
            src={brand.logo_url}
            alt=""
            className={styles.brandLogo}
            /* A logo that fails to load must leave the NAME standing, not a
               broken-image icon beside it. */
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        </span>
      ) : brand ? null : (
        <PlatformMark />
      )}

      <div>
        {/*
          *** THE INSTITUTE'S NAME, NOT OURS ***
          A non-breaking space while the lookup runs, so the card does not
          jump when the name arrives and our brand never appears on their
          page even for a frame.
        */}
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.subtitle}>{subtitle}</p>
      </div>
    </div>
  )
}
